import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { LayoutDashboard, Briefcase, Users, Settings, ChevronLeft, ChevronRight, LogOut, Building2 } from "lucide-react";
import { logout } from "../../../services/userService";
import { getCustomerCompanyDetails } from "../../../services/staffingService";
import { useUser } from "../../../context/UserContext";

const NAV_ITEMS = [
  { path: "",           label: "Home",        icon: LayoutDashboard, exact: true },
  { path: "jobs",       label: "Work Orders", icon: Briefcase,       match: "jobs" },
  { path: "facilities", label: "Facilities",  icon: Building2,       match: ["facilities", "facility"] },
  { path: "team",       label: "Team",        icon: Users,           match: "team" },
  { path: "account",    label: "Account",     icon: Settings,        match: "account" },
];

const CustomerDashboardSidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const { customerCompanyId } = useParams();
  const { userInfo } = useUser();

  const [companyName, setCompanyName] = useState("Loading...");
  const [companyInitial, setCompanyInitial] = useState("S");

  useEffect(() => {
    if (customerCompanyId) {
      getCustomerCompanyDetails(customerCompanyId)
        .then((resp) => {
          const name = resp.data?.companyLegalName || "N/A";
          setCompanyName(name);
          setCompanyInitial(name.charAt(0).toUpperCase() || "U");
        })
        .catch(() => {
          setCompanyName("Company");
          setCompanyInitial("C");
        });
    }
  }, [customerCompanyId]);

  // isOpen=true → collapsed (68px) to maintain backward-compat with existing pages
  const collapsed = isOpen;

  const isActive = (item) => {
    const fullPath = `/customer-company/${customerCompanyId}/${item.path}`.replace(/\/$/, '');
    const currentPath = location.pathname.replace(/\/$/, '');

    if (item.exact) {
      // Home: active only when on the root path
      return currentPath === `/customer-company/${customerCompanyId}` || 
             currentPath === `/customer-company/${customerCompanyId}/`;
    }
    const matches = Array.isArray(item.match) ? item.match : [item.match || item.path];
    return matches.some((token) => currentPath.startsWith(`/customer-company/${customerCompanyId}/${token}`));
  };

  const initiateLogout = async () => {
    const resp = await logout();
    window.location.href = resp.data.logoutUrl;
  };

  return (
    <aside
      className={`flex h-full flex-col border-r border-gray-200 bg-white transition-all duration-300 flex-shrink-0 font-inter ${
        collapsed ? "w-[68px]" : "w-[240px]"
      }`}
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-gray-200 px-4">
        {collapsed ? (
          <div className="flex w-full justify-center">
            <img src={require('../../_asset/logo/icon.png')} alt="Officer" className="w-auto h-8 md:h-10 object-contain" />
          </div>
        ) : (
          <Link to="/home" className="flex items-center gap-2 no-underline">
            <img src={require('../../_asset/logo/icon.png')} alt="Officer" className="w-auto h-8 md:h-10 object-contain" />
            <span className="text-lg font-bold text-[#404880]">Officer.app</span>
          </Link>
        )}
      </div>

      {/* Company pill */}
      <div className="border-b border-gray-200 p-3">
        <Link
          to="/home"
          title="Switch portals"
          className={`flex items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium transition-colors no-underline cursor-pointer ${
            collapsed ? "justify-center" : "text-gray-700 hover:bg-gray-50"
          }`}
        >
          {collapsed ? (
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#CC457D] text-white font-semibold text-sm">
              {companyInitial}
            </div>
          ) : (
            <>
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#CC457D]/10 text-[#CC457D]">
                <Building2 size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-xs font-semibold text-gray-900">{companyName}</div>
                <div className="text-[10px] text-gray-400">Customer portal</div>
              </div>
            </>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV_ITEMS.map(({ path, label, icon: Icon, match, exact }) => {
          const active = isActive({ path, match, exact });
          // Build the href: empty path → root, tab=schedule → root + search, otherwise sub-path
          const href = path === ''
            ? `/customer-company/${customerCompanyId}/`
            : path.startsWith('?')
              ? `/customer-company/${customerCompanyId}/${path}`
              : `/customer-company/${customerCompanyId}/${path}`;
          return (
            <Link
              key={path + label}
              to={href}
              title={collapsed ? label : ""}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors no-underline ${
                collapsed ? "justify-center" : ""
              } ${
                active
                  ? "bg-[#404880]/10 text-[#404880]"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <Icon size={18} strokeWidth={active ? 2.5 : 2} />
              {!collapsed && <span>{label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="px-3 pb-2">
        <button
          onClick={() => setIsOpen && setIsOpen(!isOpen)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
        >
          {collapsed ? (
            <ChevronRight size={15} />
          ) : (
            <>
              <ChevronLeft size={15} />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>

      {/* User footer */}
      <div className="border-t border-gray-200 p-3">
        <div
          className={`flex items-center gap-2 rounded-lg py-2 ${
            collapsed ? 'justify-center' : 'px-2'
          }`}
        >
          {userInfo?.profilePictureUrl ? (
            <img
              src={userInfo.profilePictureUrl}
              alt="Profile"
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#CC457D] text-white text-xs font-semibold">
              {userInfo?.firstName?.charAt(0)}
              {userInfo?.lastName?.charAt(0)}
            </div>
          )}
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="truncate text-xs font-semibold text-gray-900">
                {userInfo?.firstName} {userInfo?.lastName}
              </div>
              <div className="text-[10px] text-gray-400">Facility Manager</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={initiateLogout}
              title="Log out"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <LogOut size={15} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default CustomerDashboardSidebar;


